import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { RecentActivityItem } from '../../types/dashboard.types';

interface RecentActivityListProps {
  items: RecentActivityItem[];
  isLoading?: boolean;
}

export const RecentActivityList: React.FC<RecentActivityListProps> = ({
  items,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const formatRelativeTime = (isoString: string) => {
    try {
      const now = Date.now();
      const past = new Date(isoString).getTime();
      const diffSec = Math.floor((now - past) / 1000);

      if (diffSec < 60) return 'Just now';
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHours = Math.floor(diffMin / 60);
      if (diffHours < 24) return `${diffHours}h ago`;
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d ago`;

      return new Date(isoString).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return isoString;
    }
  };

  const getRatingBadge = (item: RecentActivityItem) => {
    if (item.action === 'REVIEW' && item.rating) {
      switch (item.rating) {
        case 'AGAIN':
          return (
            <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-rose-500/20 text-rose-300 border border-rose-500/30">
              Again
            </span>
          );
        case 'HARD':
          return (
            <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
              Hard
            </span>
          );
        case 'GOOD':
          return (
            <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Good
            </span>
          );
        case 'EASY':
          return (
            <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-sky-500/20 text-sky-300 border border-sky-500/30">
              Easy
            </span>
          );
      }
    }

    if (item.action === 'LEARNED') {
      return (
        <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
          Learned
        </span>
      );
    }

    if (item.action === 'UNLEARNED') {
      return (
        <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-700/50 text-slate-300 border border-slate-600/30">
          To Learn
        </span>
      );
    }

    return (
      <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-surface-700 text-slate-300">
        {item.action}
      </span>
    );
  };

  return (
    <div className="rounded-2xl p-6 border border-white/10 bg-surface-800/80 backdrop-blur-sm space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-white tracking-tight">Recent Activity</h3>
          <p className="text-xs text-slate-400 mt-0.5">Your latest learning and review actions</p>
        </div>
        <button
          type="button"
          id="view-all-history-btn"
          onClick={() => navigate('/review?tab=history')}
          className="text-xs text-primary-400 hover:text-primary-300 font-semibold transition-colors flex items-center gap-1"
        >
          View Full History &rarr;
        </button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="py-8 text-center text-xs text-slate-400 animate-pulse">
          Loading recent activities...
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center">
          <p className="text-xs text-slate-500">No learning activity recorded yet.</p>
        </div>
      ) : (
        <div className="divide-y divide-white/5">
          {items.map((item) => (
            <div
              key={item.id}
              className="py-3 flex items-center justify-between gap-3 hover:bg-white/[0.02] -mx-2 px-2 rounded-xl transition-colors"
            >
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">{item.word}</p>
                <p className="text-xs text-slate-400 truncate">{item.meaningVi}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {getRatingBadge(item)}
                <span className="text-[11px] text-slate-500 font-mono">
                  {formatRelativeTime(item.reviewedAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
